import { useEffect, useMemo, useState } from "react";
import * as Team from "src/state/modules/team";
import * as Eden from "src/eden/content";
import { useGetTeamEventGameStreamQuery, useGetTeamEventOpponentGameStreamQuery } from "src/state/modules/api";
import { buildGameState, BuiltGameState, SaberGameSnapshot, SABERTOOTH_ENABLED } from "src/utils/sabertooth-client";
import { PlayerMap, toSaberAgeGroup } from "src/components/pages/Events/game-stream-helpers";

const LIVE_POLL_MS = 30000;

export interface UseGameStateResult {
  built: BuiltGameState | null;
  state: SaberGameSnapshot | null;
  players: PlayerMap;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

// The opponent result also exposes which team id in the (foreign) stream is the opponent, so the
// caller can label the two sides — neither id equals our own team.id in the opponent's stream.
export interface UseOpponentGameStateResult extends UseGameStateResult {
  opponentTeamId?: string;
}

interface BuildFlags {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  disabled: boolean;
}

/**
 * Replays a GameChanger game stream through sabertooth and exposes the resulting state plus a
 * player-id → name map (names come resolved with the stream as `stream.players`, covering both
 * teams). Shared by {@link useGameState} (our own stream) and {@link useOpponentGameState} (the
 * opponent's book of the same game) — only the source query differs. `team` supplies the
 * sport/age/innings the replay needs; for the opponent stream those are identical (same game).
 */
function useBuildFromStream(
  team: Team.Team | undefined,
  stream: Eden.GameStream | undefined,
  flags: BuildFlags
): UseGameStateResult {
  const [built, setBuilt] = useState<BuiltGameState | null>(null);
  const [buildError, setBuildError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (flags.disabled || !team || !stream) {
      setBuilt(null);
      return;
    }
    buildGameState(stream, {
      sport: team.sport,
      ageGroup: toSaberAgeGroup(team.ageGroup),
      inningsPerGame: team.inningsPerGame,
    })
      .then((result) => {
        if (cancelled) return;
        setBuilt(result);
        setBuildError(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("Failed to build game state from sabertooth", err);
        setBuilt(null);
        setBuildError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [flags.disabled, stream, team?.sport, team?.ageGroup, team?.inningsPerGame]);

  const players: PlayerMap = useMemo(() => {
    const map: PlayerMap = {};
    for (const [id, p] of Object.entries(stream?.players ?? {})) {
      map[id] = { id, first_name: p.first_name, last_name: p.last_name, number: p.number };
    }
    return map;
  }, [stream?.players]);

  return {
    built,
    state: built?.state ?? null,
    players,
    isLoading: flags.isLoading,
    isFetching: flags.isFetching,
    isError: flags.isError || buildError,
  };
}

/**
 * Fetches an event's GameChanger game stream, replays it through sabertooth, and exposes the
 * resulting state plus a player-id → name map (for name lookups). The name map is resolved and
 * cached server-side and returned inline with the stream (`stream.players`), so it covers both
 * teams — opponents included — without us pre-copying opponent rosters. Polls while the game is
 * live so the Box Score / Plays tabs update in near-real-time.
 *
 * `team` may be undefined (e.g. still loading) and `opts.enabled` can gate the work off entirely
 * (e.g. only build for final games) — in both cases the queries are skipped and nothing is built.
 */
export function useGameState(
  team: Team.Team | undefined,
  eventId: string,
  opts?: { live?: boolean; enabled?: boolean }
): UseGameStateResult {
  const disabled = !SABERTOOTH_ENABLED || !team || opts?.enabled === false;
  const args = { teamID: team?.id ?? "", eventID: eventId };

  const { data: stream, isLoading, isFetching, isError } = useGetTeamEventGameStreamQuery(args, {
    skip: disabled,
    ...(opts?.live ? { pollingInterval: LIVE_POLL_MS, skipPollingIfUnfocused: true } : {}),
  });

  return useBuildFromStream(team, stream, { isLoading, isFetching, isError, disabled });
}

/**
 * Like {@link useGameState}, but for the *opponent's* scorebook of the same game. In GameChanger
 * each team keeps its own book, so the opponent's real team has an independent stream; the backend
 * resolves and matches it (see `GetOpponentEventGameStreamAsync`). A 404 (no progenitor team or no
 * matching game) surfaces as `isError`, which the caller renders as "not available". Gated off by
 * default — pass `{ enabled: true }` (e.g. only when the modal is open) so we never pay the
 * GameChanger fan-out unless the user asks for it.
 */
export function useOpponentGameState(
  team: Team.Team | undefined,
  eventId: string,
  opts?: { enabled?: boolean }
): UseOpponentGameStateResult {
  const disabled = !SABERTOOTH_ENABLED || !team || opts?.enabled === false;
  const args = { teamID: team?.id ?? "", eventID: eventId };

  const { data: stream, isLoading, isFetching, isError } = useGetTeamEventOpponentGameStreamQuery(args, {
    skip: disabled,
  });

  const result = useBuildFromStream(team, stream, { isLoading, isFetching, isError, disabled });
  return { ...result, opponentTeamId: stream?.opponent_team_id };
}
